import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { TeamProvider } from './contexts/TeamContext';
import { ProtectedRoute } from './components/ProtectedRoute';

// Pages
import LoginPage from './pages/Login';
import RegisterPage from './pages/Register';
import DashboardPage from './pages/Dashboard';
import MachinesPage from './pages/Machines';
import MachineDetailPage from './pages/MachineDetail';
import InstancesPage from './pages/Instances';
import InstanceDetailPage from './pages/InstanceDetail';
import InstanceConfigPage from './pages/InstanceConfig';
import EventsPage from './pages/Events';
import SettingsPage from './pages/Settings';
import DiscoverInstancesPage from './pages/DiscoverInstances';
import ChatPage from './pages/Chat';
import ProfilePage from './pages/Profile';
import TeamPage from './pages/Team';
import AcceptInvitationPage from './pages/AcceptInvitation';
import BillingPage from './pages/Billing';
import GettingStartedPage from './pages/GettingStarted';
import DownloadsPage from './pages/Downloads';
import AppleCallbackPage from './pages/AppleCallback';
import ConnectionsPage from './pages/Connections';

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: 60 * 1000, // 1 minute
			refetchOnWindowFocus: false,
		},
	},
});

function App() {
	return (
		<QueryClientProvider client={queryClient}>
			<ThemeProvider>
				<AuthProvider>
					<TeamProvider>
						<BrowserRouter future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
							<Routes>
								{/* Public routes */}
								<Route path="/login" element={<LoginPage />} />
								<Route path="/register" element={<RegisterPage />} />
								<Route path="/team/accept" element={<AcceptInvitationPage />} />
								<Route path="/apple-callback" element={<AppleCallbackPage />} />

							{/* Admin-only routes */}
							<Route path="/dashboard" element={<ProtectedRoute adminOnly><DashboardPage /></ProtectedRoute>} />
							<Route path="/machines" element={<ProtectedRoute adminOnly><MachinesPage /></ProtectedRoute>} />
							<Route path="/machines/:agentId" element={<ProtectedRoute adminOnly><MachineDetailPage /></ProtectedRoute>} />
							<Route path="/instances" element={<ProtectedRoute adminOnly><InstancesPage /></ProtectedRoute>} />
							<Route path="/instances/:id" element={<ProtectedRoute adminOnly><InstanceDetailPage /></ProtectedRoute>} />
							<Route path="/instances/:instanceId/config" element={<ProtectedRoute adminOnly><InstanceConfigPage /></ProtectedRoute>} />
							<Route path="/discover" element={<ProtectedRoute adminOnly><DiscoverInstancesPage /></ProtectedRoute>} />
							<Route path="/events" element={<ProtectedRoute adminOnly><EventsPage /></ProtectedRoute>} />
							<Route path="/billing" element={<ProtectedRoute adminOnly><BillingPage /></ProtectedRoute>} />
							<Route path="/settings" element={<ProtectedRoute adminOnly><SettingsPage /></ProtectedRoute>} />

							<Route path="/team" element={<ProtectedRoute adminOnly><TeamPage /></ProtectedRoute>} />

							{/* All-role routes */}
							<Route path="/getting-started" element={<ProtectedRoute><GettingStartedPage /></ProtectedRoute>} />
							<Route path="/downloads" element={<ProtectedRoute><DownloadsPage /></ProtectedRoute>} />
							<Route path="/chat" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
							<Route path="/connections" element={<ProtectedRoute><ConnectionsPage /></ProtectedRoute>} />
							<Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />

								{/* Default redirect */}
								<Route path="/" element={<Navigate to="/chat" replace />} />
							</Routes>
						</BrowserRouter>
					</TeamProvider>
				</AuthProvider>
			</ThemeProvider>
		</QueryClientProvider>
	);
}

export default App;
